//@flow
import * as React from 'react';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import { translate } from 'react-i18next';
import { themr } from 'react-css-themr';

import Button, { ButtonTypes } from 'common/components/core/button';
import { dashboardColors } from 'common/enums/ColorEnum';
import { compose } from 'recompose';
import Icon from 'common/components/core/icon';
import theme from './ErrorScreen.scss';
import DataUtils from 'common/utils/DataUtils';
import isFunction from 'lodash/isFunction';

const DefaultErrorCode = ({ errorCodeText, theme }) => {
    return !DataUtils.isEmpty(errorCodeText) ? (
        <div className={theme.errorCodeText}>{errorCodeText}</div>
    ) : null;
};
const DefaultContentWrap = ({ children, theme, maxContentWidth }) => {
    return (
        <div
            className={theme.messageContainer}
            style={{ maxWidth: maxContentWidth }}
        >
            {children}
        </div>
    );
};
const DefaultTitle = ({ title, theme }) => {
    return !DataUtils.isEmpty(title) ? (
        <div className={theme.title}>{title}</div>
    ) : null;
};
const DefaultSubtitle = ({ subtitle, theme }) => {
    return !DataUtils.isEmpty(subtitle) ? (
        <div className={theme.subtitle}>{subtitle}</div>
    ) : null;
};
const DefaultActions = ({ actionUrl, actionLabel, theme }) => {
    if (!actionUrl) {
        return null;
    }
    return (
        <Link to={actionUrl}>
            <Button
                buttonType={ButtonTypes.Solid}
                semibold
                uppercase
                color={dashboardColors.darkSkyBlue.name}
                label={actionLabel}
                theme={theme}
            />
        </Link>
    );
};
const errorScreenFactory = ({
    Actions = DefaultActions,
    ErrorCode = DefaultErrorCode,
    Title = DefaultTitle,
    Subtitle = DefaultSubtitle,
    ContentWrap = DefaultContentWrap
}) => {
    const renderChildren = props => {
        const {
            iconProps,
            errorCodeText,
            withIcon,
            title,
            subtitle,
            t,
            theme,
            actionLabel,
            actionUrl
        } = props;
        const { icon, ...rest } = iconProps || {};
        return (
            <ContentWrap theme={theme}>
                <ErrorCode theme={theme} errorCodeText={errorCodeText} />
                {withIcon ? <Icon icon={icon} theme={theme} {...rest} /> : null}
                <Title theme={theme} title={title} />
                <Subtitle theme={theme} subtitle={subtitle} />
                {Actions ? (
                    <Actions
                        t={t}
                        actionLabel={actionLabel}
                        actionUrl={actionUrl}
                        theme={theme}
                    />
                ) : null}
            </ContentWrap>
        );
    };
    const ErrorScreen = ({
        theme,
        absolute,
        backgroundUrl = '/img/404_not_found.png',
        withIcon,
        children = renderChildren,
        ...others
    }) => {
        const backgroundStyle =
            backgroundUrl && !withIcon
                ? { backgroundImage: `url(${backgroundUrl})` }
                : {};

        let errorDisplay = children;
        if (isFunction(children)) {
            errorDisplay = children({ ...others, theme, withIcon });
        }
        return (
            <div
                style={backgroundStyle}
                className={classnames(theme.container, {
                    [theme.absolute]: absolute,
                    [theme.withIcon]: withIcon
                })}
            >
                {errorDisplay}
            </div>
        );
    };

    return compose(themr('ErrorScreen', theme), translate())(ErrorScreen);
};
const ErrorScreen = errorScreenFactory({});

export { ErrorScreen };
export default ErrorScreen;
